import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Wrench, Plus, Trash2, Edit2, Loader2, Search, MapPin, Package,
  ArrowRight, Box, Warehouse, Truck, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORIES = {
  'werkzeug': 'Werkzeug', 'maschine': 'Maschine', 'verbrauch': 'Verbrauchsmaterial',
  'schutz': 'Schutzausrüstung', 'elektro': 'Elektrowerkzeug', 'sonstiges': 'Sonstiges'
};

const LOCATION_TYPES = {
  'lager': 'Im Lager', 'kunde': 'Beim Kunden', 'auto': 'Im Fahrzeug', 'baustelle': 'Auf Baustelle', 'mitarbeiter': 'Bei Mitarbeiter'
};

const BOX_COLORS = [
  { value: 'rot', label: 'Rot', color: '#ef4444' },
  { value: 'blau', label: 'Blau', color: '#3b82f6' },
  { value: 'grün', label: 'Grün', color: '#22c55e' },
  { value: 'gelb', label: 'Gelb', color: '#eab308' },
  { value: 'orange', label: 'Orange', color: '#f97316' },
  { value: 'schwarz', label: 'Schwarz', color: '#374151' },
  { value: 'weiß', label: 'Weiß', color: '#e5e7eb' },
  { value: '', label: 'Keine', color: 'transparent' }
];

const getLocationIcon = (type) => {
  switch (type) {
    case 'lager': return <Warehouse className="w-4 h-4 text-blue-600" />;
    case 'kunde': return <MapPin className="w-4 h-4 text-green-600" />;
    case 'auto': return <Truck className="w-4 h-4 text-yellow-600" />;
    case 'baustelle': return <Package className="w-4 h-4 text-orange-600" />;
    default: return <Box className="w-4 h-4 text-gray-600" />;
  }
};

const getLocationText = (item) => {
  const parts = [LOCATION_TYPES[item.location_type] || item.location_type];
  if (item.location_type === 'lager') {
    if (item.shelf_number) parts.push(`Regal ${item.shelf_number}`);
    if (item.box_color) parts.push(`${BOX_COLORS.find(b => b.value === item.box_color)?.label || item.box_color} Box`);
  }
  if (item.location_type === 'kunde' && item.customer_name) parts.push(item.customer_name);
  if (item.location_detail) parts.push(item.location_detail);
  return parts.join(' - ');
};

const boxColorDot = (color) => {
  const c = BOX_COLORS.find(b => b.value === color);
  if (!c || !c.value) return null;
  return <span className="inline-block w-3 h-3 rounded-full border" style={{ backgroundColor: c.color }} />;
};

const AdminWerkzeug = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');

  const [form, setForm] = useState({
    name: '', description: '', category: 'werkzeug', quantity: '1',
    location_type: 'lager', location_detail: '', shelf_number: '', box_color: '', customer_name: ''
  });

  const [moveForm, setMoveForm] = useState({
    location_type: 'lager', location_detail: '', shelf_number: '', box_color: '', customer_name: ''
  });

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterLocation !== 'all') params.location_type = filterLocation;
      if (searchQuery) params.search = searchQuery;
      const res = await api.get('/admin/inventory', { params });
      setItems(res.data || []);
    } catch (e) { toast.error('Fehler beim Laden'); }
    setLoading(false);
  };

  useEffect(() => { loadItems(); }, [filterCategory, filterLocation]);

  const handleSearch = () => loadItems();

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', description: '', category: 'werkzeug', quantity: '1', location_type: 'lager', location_detail: '', shelf_number: '', box_color: '', customer_name: '' });
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '', description: item.description || '', category: item.category || 'werkzeug',
      quantity: String(item.quantity || 1), location_type: item.location_type || 'lager',
      location_detail: item.location_detail || '', shelf_number: item.shelf_number || '',
      box_color: item.box_color || '', customer_name: item.customer_name || ''
    });
    setShowDialog(true);
  };

  const openMove = (item) => {
    setMoveTarget(item);
    setMoveForm({
      location_type: item.location_type || 'lager', location_detail: item.location_detail || '',
      shelf_number: item.shelf_number || '', box_color: item.box_color || '', customer_name: item.customer_name || ''
    });
    setShowMoveDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist Pflicht'); return; }
    const data = { ...form, quantity: parseInt(form.quantity) || 1 };
    try {
      if (editing) { await api.put(`/admin/inventory/${editing.id}`, data); toast.success('Aktualisiert'); }
      else { await api.post('/admin/inventory', data); toast.success('Artikel erstellt'); }
      setShowDialog(false); loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
  };

  const handleMove = async () => {
    try {
      await api.put(`/admin/inventory/${moveTarget.id}/move`, moveForm);
      toast.success('Standort aktualisiert');
      setShowMoveDialog(false); loadItems();
    } catch (e) { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Artikel wirklich löschen?')) return;
    try { await api.delete(`/admin/inventory/${id}`); toast.success('Gelöscht'); loadItems(); } catch (e) { toast.error('Fehler'); }
  };

  // Group by location
  const groupedByLocation = items.reduce((acc, item) => {
    const key = item.location_type || 'sonstiges';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-[#d97706] to-[#f59e0b] rounded-xl p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3" data-testid="werkzeug-title">
                <Wrench className="w-8 h-8" /> Werkzeug & Lager
              </h1>
              <p className="text-white/80 mt-1">Werkzeug und Material verfolgen - wo liegt was?</p>
            </div>
            <Button className="bg-white text-[#d97706] hover:bg-gray-100" onClick={openNew} data-testid="new-item-btn">
              <Plus className="w-4 h-4 mr-2" /> Neuer Artikel
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-6xl mx-auto mb-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
        {Object.entries(LOCATION_TYPES).map(([key, label]) => {
          const count = (groupedByLocation[key] || []).length;
          return (
            <Card key={key} className={`cursor-pointer transition-all ${filterLocation === key ? 'ring-2 ring-amber-400' : ''}`} onClick={() => setFilterLocation(filterLocation === key ? 'all' : key)}>
              <CardContent className="p-3 text-center">
                <div className="flex justify-center mb-1">{getLocationIcon(key)}</div>
                <p className="text-xl font-bold text-gray-900">{count}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search & Filter */}
      <div className="max-w-6xl mx-auto mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Suche nach Werkzeug, Standort..." className="pl-10" data-testid="search-inventory" />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Alle Kategorien" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle Kategorien</SelectItem>{Object.entries(CATEGORIES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="ghost" size="icon" onClick={loadItems}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Items List */}
      <div className="max-w-6xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="text-center py-8 text-gray-500"><Wrench className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>Keine Artikel gefunden</p><Button variant="outline" className="mt-3" onClick={openNew}><Plus className="w-4 h-4 mr-2" /> Ersten Artikel anlegen</Button></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {items.map(item => (
              <Card key={item.id} data-testid={`item-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                    <div className="flex-1 mb-2 sm:mb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900">{item.name}</h3>
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">{CATEGORIES[item.category] || item.category}</span>
                        {item.quantity > 1 && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">x{item.quantity}</span>}
                      </div>
                      {item.description && <p className="text-sm text-gray-500 mb-1">{item.description}</p>}
                      <div className="flex items-center gap-2 text-sm">
                        {getLocationIcon(item.location_type)}
                        <span className="text-gray-600">{getLocationText(item)}</span>
                        {boxColorDot(item.box_color)}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => openMove(item)} title="Umlagern" className="text-amber-600 border-amber-200 hover:bg-amber-50" data-testid={`move-${item.id}`}>
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)}><Edit2 className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Artikel bearbeiten' : 'Neuer Artikel'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="z.B. Staubsauger, Röhrstange" className="mt-1" data-testid="input-item-name" />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Weitere Details..." className="mt-1" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kategorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORIES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Anzahl</Label>
                <Input type="number" value={form.quantity} onChange={(e) => setForm(p => ({ ...p, quantity: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div className="border-t pt-3"><p className="text-sm font-medium text-gray-700 mb-2">Standort</p></div>
            <div>
              <Label>Standort-Typ</Label>
              <Select value={form.location_type} onValueChange={(v) => setForm(p => ({ ...p, location_type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LOCATION_TYPES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.location_type === 'lager' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Regal-Nr.</Label>
                  <Input value={form.shelf_number} onChange={(e) => setForm(p => ({ ...p, shelf_number: e.target.value }))} placeholder="z.B. 03" className="mt-1" />
                </div>
                <div>
                  <Label>Box-Farbe</Label>
                  <Select value={form.box_color || 'none'} onValueChange={(v) => setForm(p => ({ ...p, box_color: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Keine" /></SelectTrigger>
                    <SelectContent>{BOX_COLORS.map(b => (
                      <SelectItem key={b.value || 'none'} value={b.value || 'none'}>
                        <span className="flex items-center gap-2">{b.value && <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: b.color }} />}{b.label}</span>
                      </SelectItem>
                    ))}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {(form.location_type === 'kunde' || form.location_type === 'baustelle') && (
              <div>
                <Label>Kundenname / Baustelle</Label>
                <Input value={form.customer_name} onChange={(e) => setForm(p => ({ ...p, customer_name: e.target.value }))} placeholder="z.B. Familie Müller" className="mt-1" />
              </div>
            )}
            <div>
              <Label>Details / Beschreibung</Label>
              <Input value={form.location_detail} onChange={(e) => setForm(p => ({ ...p, location_detail: e.target.value }))} placeholder="z.B. Keller links, Garage" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#d97706] hover:bg-[#b45309] text-white" data-testid="save-item-btn">{editing ? 'Speichern' : 'Anlegen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Standort ändern</DialogTitle></DialogHeader>
          {moveTarget && (
            <div className="space-y-3 py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="font-medium text-gray-900">{moveTarget.name}</p>
                <p className="text-sm text-gray-500">Aktuell: {getLocationText(moveTarget)}</p>
              </div>
              <div>
                <Label>Standort-Typ</Label>
                <Select value={moveForm.location_type} onValueChange={(v) => setMoveForm(p => ({ ...p, location_type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(LOCATION_TYPES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {moveForm.location_type === 'lager' && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Regal-Nr.</Label><Input value={moveForm.shelf_number} onChange={(e) => setMoveForm(p => ({ ...p, shelf_number: e.target.value }))} placeholder="z.B. 03" className="mt-1" /></div>
                  <div><Label>Box-Farbe</Label>
                    <Select value={moveForm.box_color || 'none'} onValueChange={(v) => setMoveForm(p => ({ ...p, box_color: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Keine" /></SelectTrigger>
                      <SelectContent>{BOX_COLORS.map(b => (
                        <SelectItem key={b.value || 'none'} value={b.value || 'none'}>
                          <span className="flex items-center gap-2">{b.value && <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: b.color }} />}{b.label}</span>
                        </SelectItem>
                      ))}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {(moveForm.location_type === 'kunde' || moveForm.location_type === 'baustelle') && (
                <div><Label>Kundenname / Baustelle</Label><Input value={moveForm.customer_name} onChange={(e) => setMoveForm(p => ({ ...p, customer_name: e.target.value }))} placeholder="z.B. Familie Müller" className="mt-1" /></div>
              )}
              <div><Label>Details / Beschreibung</Label><Input value={moveForm.location_detail} onChange={(e) => setMoveForm(p => ({ ...p, location_detail: e.target.value }))} placeholder="z.B. Keller links, Garage" className="mt-1" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Abbrechen</Button>
            <Button onClick={handleMove} className="bg-[#d97706] hover:bg-[#b45309] text-white" data-testid="save-move-btn">Umlagern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWerkzeug;
